import { FC, useMemo } from "react";

import { useUseCaseLabels } from "@/components/studies/useCaseRequirements";
import { useLocalization } from "@/hooks";
import { QuestionResults, Section } from "@/types/resources";
import AdCreativeReport, { AdCreativeReportData } from "./AdCreativeReport";
import { cookTopBoxScoring } from "./_topBoxScoring";

interface AdCreativeSectionResultsProps {
  data: QuestionResults[];
  section: Section;
}

/* ── Cooker ─────────────────────────────────────────────── */

/** Templated ad-creative sections carry liking / brand fit / interest
    in a fixed order. Translated labels live here; ad names come from
    `section.stimuli`. */
const AdCreativeSectionResults: FC<AdCreativeSectionResultsProps> = ({
  data,
  section,
}) => {
  const { i18n } = useLocalization();
  const labels = useUseCaseLabels(section.use_case);

  const reportData: AdCreativeReportData = useMemo(() => {
    const metricLabels = [
      i18n.t("useCasePage.adCreative.sampleReport.metrics.liking"),
      i18n.t("useCasePage.adCreative.sampleReport.metrics.brandFit"),
      i18n.t("useCasePage.adCreative.sampleReport.metrics.interest"),
    ];
    const scored = cookTopBoxScoring(data, section, metricLabels, (i) =>
      labels.stimulusPlaceholder(i + 1)
    );
    return {
      ads: scored.entities,
      metrics: scored.metrics,
      perCellSampleSize: scored.perCellSampleSize,
      respondentCount: scored.respondentCount,
      topBoxCounts: scored.topBoxCounts,
    };
  }, [data, section, i18n, labels]);

  return <AdCreativeReport data={reportData} />;
};

export default AdCreativeSectionResults;
